import { useCallback, useEffect, useRef, useState } from 'react';
import { getAllFocusable } from '../../../util/focus';
import { useOnOutsideMouseDown } from '../../PropertyPaneLayout/useOnOutsideMouseDown';
export const useInteractiveTile = props => {
  const hasFocusRef = useRef(false);
  const [isInteractive, setIsInteractive] = useState(false);
  // We need to turn focus lock off when user clicks outside.
  useOnOutsideMouseDown(props.tileRef, useCallback(() => {
    setIsInteractive(false);
  }, []));
  const onBlur = e => {
    if (!e.currentTarget.contains(e.relatedTarget) && !isInteractive && hasFocusRef.current && !props.isMenuOpen) {
      hasFocusRef.current = false;
    }
  };

  // Set focus on the TileContainer after switching from interactive mode to browse mode
  useEffect(() => {
    if (hasFocusRef.current && !isInteractive && props.tileRef.current !== document.activeElement) {
      props.tileRef.current?.focus({
        preventScroll: true
      });
    }
  }, [isInteractive, props.tileRef]);

  // Toggle the focusability of the body/footer/custom elements based on suppressCustomContentFocus or isInteractive value
  // The body/footer section may contain elements that focuslock doesn't handle correctly. E.g. input tag with
  // readonly attribute. We need to manually toggle tabindex for these.
  useEffect(() => {
    const focusableElements = [];
    for (const customContentRef of props.customContentRefs ?? []) {
      if (!customContentRef.current) continue;
      focusableElements.push(...getAllFocusable(customContentRef.current));
    }
    if (props.disableInteractiveMode) {
      if (props.suppressCustomContentFocus) {
        // When the interactive mode is disabled, the inner elements should not receive focus by default
        // consumers can make them focusable by setting the container tabIndex to -1
        for (const element of focusableElements) {
          element.setAttribute('tabindex', '-1');
        }
        return () => {
          for (const element of focusableElements) {
            element.setAttribute('tabindex', '0');
          }
        };
      }
      return;
    }
    for (const element of focusableElements) {
      element.setAttribute('tabindex', isInteractive ? '0' : '-1');
    }
    return () => {
      for (const element of focusableElements) {
        element.setAttribute('tabindex', '0');
      }
    };
  }, [props.customContentRefs, isInteractive, props.disableInteractiveMode, props.suppressCustomContentFocus]);
  return {
    isInteractive,
    onClick: () => {
      // Implicitly change to interactive mode.
      setIsInteractive(true);
    },
    onFocus: () => {
      hasFocusRef.current = true;
    },
    onBlur,
    onKeyDown: e => {
      if (e.defaultPrevented) return;
      if (e.key === 'F2') {
        setIsInteractive(prev => !prev);
        e.preventDefault();
      } else if (isInteractive) {
        if (e.key === 'Escape') {
          // Escape cancels interactive mode.
          setIsInteractive(false);
          e.preventDefault();
        }
        // When in interactive mode, no keyboard event should be handled outside of the tile
        e.stopPropagation();
      }
    }
  };
};
//# sourceMappingURL=useInteractiveTile.js.map